import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function SouthernGulfIslandsTrip() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "southern-gulf-islands-trip";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "how-to-get-there": "How to Get There",
    "top-locations": "Top Locations",
    "itineraries": "Best Itineraries",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.bcferries.com/routes-fares/schedules",
      label: "BC Ferries – Schedules & Reservations",
    },
    {
      url: "https://parks.canada.ca/pn-np/bc/gulf",
      label: "Parks Canada – Gulf Islands National Park Reserve",
    },
    {
      url: "https://www.hellobc.com/places-to-go/gulf-islands/",
      label: "HelloBC – Gulf Islands Travel Guide",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We have 7 distinct spots from the provided conversation.
  // We'll match each location ID (e.g., 'ganges-1', 'ruckle-2', etc.) with lat/lng.
  // The "id" matches the ref used in the article's text. The popupText is the short name.
  const locations = [
    {
      id: "ganges-salt-spring",
      lat: 48.84972,
      lng: -123.50111,
      popupText: "Ganges, Salt Spring Island",
    },
    {
      id: "ruckle-provincial-park",
      lat: 48.78139,
      lng: -123.38944,
      popupText: "Ruckle Provincial Park",
    },
    {
      id: "mount-maxwell",
      lat: 48.80490,
      lng: -123.51530,
      popupText: "Mount Maxwell Provincial Park",
    },
    {
      id: "montague-harbour",
      lat: 48.90056,
      lng: -123.40500,
      popupText: "Montague Harbour, Galiano",
    },
    {
      id: "mount-norman",
      lat: 48.75937,
      lng: -123.24015,
      popupText: "Mount Norman, Pender Island",
    },
    {
      id: "east-point-saturna",
      lat: 48.78301,
      lng: -123.04546,
      popupText: "East Point, Saturna Island",
    },
    {
      id: "georgina-point-mayne",
      lat: 48.87250,
      lng: -123.29230,
      popupText: "Georgina Point, Mayne Island",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>The Ultimate Southern Gulf Islands Escape From Vancouver</title>
        <meta
          name="description"
          content="Plan a Southern Gulf Islands adventure from Vancouver with top locations, island-hopping tips, FAQs, best itineraries, and local regulations."
        />
        <meta
          name="keywords"
          content="Southern Gulf Islands, Vancouver, island-hopping, BC Ferries, travel tips"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/southern-gulf-islands-trip.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="The Ultimate Southern Gulf Islands Escape From Vancouver"
        />
        <meta
          property="og:description"
          content="Explore Salt Spring, Galiano, Pender, Mayne, Saturna, and more with this in-depth travel guide from Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/southern-gulf-islands-trip.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/southern-gulf-islands-trip.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="The Ultimate Southern Gulf Islands Escape From Vancouver"
        />
        <meta
          name="twitter:description"
          content="Plan a ferry or seaplane trip to explore the Gulf Islands. Find top spots, local rules, and must-see highlights."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/southern-gulf-islands-trip.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Scenic view of the Gulf Islands"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Looking for a tranquil island escape within easy reach of
              Vancouver? The Southern Gulf Islands—Salt Spring, Galiano, Pender,
              Mayne, and Saturna—offer lush forests, artisan communities, and
              breathtaking coastal vistas. Whether you’re seeking a relaxed
              family getaway or an adventurous, multi-island tour, these islands
              deliver an unforgettable experience. In this guide, we’ll cover
              how to get there, the top locations to visit, sample itineraries,
              local regulations, and key FAQs to help you plan the perfect trip.
            </p>

            {/* SECTION: How to Get There */}
            <h2 id="how-to-get-there">How to Get There</h2>
            <p>
              Most travelers opt to take{" "}
              <strong>BC Ferries from Tsawwassen</strong> (near Vancouver) to
              the Gulf Islands. Ferries run frequently, with approximate one-way
              fares starting around CAD $18 per foot passenger and CAD $60+ for
              vehicles (reservations recommended, especially in peak season).
              Alternatively, you can travel by <strong>seaplane</strong> from
              downtown Vancouver via companies like Harbour Air, offering a
              scenic 35-minute flight straight to Salt Spring or other islands
              (by charter). For the boating-inclined, private vessels and
              charters are also an option, with plenty of marinas and anchorages
              scattered throughout the islands.
            </p>
            <ul>
              <li>Ferry schedule & fares vary seasonally.</li>
              <li>Arrive 30–60 minutes before departure in peak summer.</li>
              <li>Seaplane flights: ~CAD $150–200 one-way, strict baggage limits.</li>
              <li>For private boats, watch for strong currents in places like Active Pass.</li>
            </ul>

            {/* SECTION: Top Locations */}
            <h2 id="top-locations">Top Locations & Attractions</h2>
            <p>
              Each island boasts unique charms, from bustling artisan markets to
              tranquil lighthouses. Below are some must-see spots, complete with
              coordinates (WGS84) so you can easily plan your route.
            </p>

            {/* 1) Ganges, Salt Spring Island */}
            <h3>1. Ganges, Salt Spring Island</h3>
            <p
              ref={(el) => setSpotRef("ganges-salt-spring", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={48.84972}
                coordX={-123.50111}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <strong>Latitude: 48.84972, Longitude: -123.50111</strong>
            </p>
            <p>
              The heart of Salt Spring Island, Ganges is a vibrant seaside
              village brimming with art galleries, boutiques, and a lively
              Saturday Market (April–October). Stroll the waterfront boardwalk,
              rent a kayak, or indulge in local artisan cheese. Perfect for
              soaking up island life.
            </p>

            {/* 2) Ruckle Provincial Park */}
            <h3>2. Ruckle Provincial Park, Salt Spring Island</h3>
            <p
              ref={(el) => setSpotRef("ruckle-provincial-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={48.78139}
                coordX={-123.38944}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <strong>Latitude: 48.78139, Longitude: -123.38944</strong>
            </p>
            <p>
              Located on the southeastern tip of Salt Spring, Ruckle Park offers
              oceanfront campsites, heritage farm buildings, and lovely hiking
              trails. Watch for wildlife like otters and bald eagles along the
              rocky shoreline. Overnight camping is about CAD $20 per tent site,
              first-come, first-served.
            </p>

            {/* 3) Mount Maxwell Provincial Park */}
            <h3>3. Mount Maxwell Provincial Park (Baynes Peak), Salt Spring Island</h3>
            <p
              ref={(el) => setSpotRef("mount-maxwell", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={48.80490}
                coordX={-123.51530}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <strong>Latitude: 48.80490, Longitude: -123.51530</strong>
            </p>
            <p>
              Enjoy panoramic views from Baynes Peak, the highest point on Salt
              Spring Island. The steep gravel access road is best for higher
              clearance vehicles. You’ll be rewarded with sweeping vistas of the
              Gulf Islands, Vancouver Island, and beyond.
            </p>

            {/* 4) Montague Harbour, Galiano Island */}
            <h3>4. Montague Harbour Marine Provincial Park, Galiano Island</h3>
            <p
              ref={(el) => setSpotRef("montague-harbour", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={48.90056}
                coordX={-123.40500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <strong>Latitude: 48.90056, Longitude: -123.40500</strong>
            </p>
            <p>
              Famous for its white-shell beach and calm bay, Montague Harbour is
              a favorite spot for picnics, kayaking, and sunset watching. The
              park has a scenic campground (CAD $18 per night) and mooring
              buoys. Look out for the “Hummingbird Pub Bus” in summer—a quirky
              local shuttle experience.
            </p>

            {/* 5) Mount Norman, Pender Island */}
            <h3>5. Mount Norman (Gulf Islands National Park), Pender Island</h3>
            <p
              ref={(el) => setSpotRef("mount-norman", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={48.75937}
                coordX={-123.24015}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <strong>Latitude: 48.75937, Longitude: -123.24015</strong>
            </p>
            <p>
              Pender’s highest peak offers a moderate hike with a wooden viewing
              platform at the summit, revealing stunning island panoramas. The
              trailhead starts near Canal Road; it’s about a 30–45 minute climb.
              Pair it with a quick visit to nearby Beaumont Marine Park for a
              secluded cove.
            </p>

            {/* 6) East Point, Saturna Island */}
            <h3>6. East Point Lighthouse, Saturna Island</h3>
            <p
              ref={(el) => setSpotRef("east-point-saturna", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={48.78301}
                coordX={-123.04546}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <strong>Latitude: 48.78301, Longitude: -123.04546</strong>
            </p>
            <p>
              This remote lighthouse perch is part of Gulf Islands National Park
              Reserve. A prime location for land-based whale watching—especially
              orcas—during summer. Enjoy coastal bluffs, a small museum in the
              Fog Alarm Building, and dramatic views of swirling currents at
              “Boiling Reef.”
            </p>

            {/* 7) Georgina Point, Mayne Island */}
            <h3>7. Georgina Point Lighthouse, Mayne Island</h3>
            <p
              ref={(el) => setSpotRef("georgina-point-mayne", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <Marker
                coordY={48.87250}
                coordX={-123.29230}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <strong>Latitude: 48.87250, Longitude: -123.29230</strong>
            </p>
            <p>
              Overlooking Active Pass, Georgina Point offers picnic areas,
              maritime history, and close-up ferry sightings as they navigate
              through this narrow channel. Look for seals, sea lions, and
              occasionally whales from the shore.
            </p>

            {/* SECTION: Itineraries */}
            <h2 id="itineraries">Best Itineraries</h2>
            <p>
              Whether you’re an adventurer seeking multiple islands or a family
              wanting a relaxed trip, here are sample plans:
            </p>
            <ul>
              <li>
                <strong>Adventurers (5–7 days)</strong>: Camp at Ruckle,
                then hop to Pender, Saturna, Mayne, Galiano by inter-island
                ferries. Hike Mount Norman, kayak Montague Harbour, and chase
                sunset views at East Point.
              </li>
              <li>
                <strong>Families (3–4 days)</strong>: Base yourself on Salt
                Spring. Explore Ganges, visit the farmers’ market, enjoy a farm
                tour or short hike, and take a day trip to Pender or Galiano if
                time allows.
              </li>
              <li>
                <strong>Budget Travelers</strong>: Walk on BC Ferries with
                a backpack; camp at Montague Harbour, Ruckle Park, or Prior
                Centennial Campground on Pender. Hitchhike or bike to explore
                each island’s highlights.
              </li>
            </ul>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              <strong>Ferry Etiquette</strong>: Arrive early for
              reservations, especially in summer. Foot passengers can often
              board without booking, but capacity can be limited.
            </p>
            <p>
              <strong>Wildlife & Environment</strong>: Observe "leave no
              trace" principles—pack out your garbage, stick to marked trails,
              and never feed wildlife. Maintain 100m–200m distance from marine
              mammals when boating or kayaking.
            </p>
            <p>
              <strong>Camping & Fires</strong>: Many provincial parks have
              fire bans during dry spells; check notices. Only use designated
              fire rings when allowed, and fully extinguish before leaving.
            </p>
            <p>
              <strong>Private Property</strong>: Respect signage and stay
              off private driveways or beaches that are not clearly marked as
              public.
            </p>
            <p>
              <strong>Water Conservation</strong>: Islands often face
              droughts; take short showers, turn off taps, and comply with any
              local water restrictions.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Which island should I visit first?</h3>
              <p>
                Salt Spring is the most popular and has the most frequent ferry
                service, making it an easy entry point. From there, you can hop
                to Pender, Galiano, Mayne, or Saturna.
              </p>

              <h3>2. Do I need a car to explore these islands?</h3>
              <p>
                Not necessarily. Salt Spring has a community bus, while the
                smaller islands have limited transit. Many travelers hitchhike
                or bring/rent bicycles. However, a car offers more convenience
                for families and remote spots.
              </p>

              <h3>3. When is the best time to go?</h3>
              <p>
                Summer (June–September) provides the best weather and most ferry
                options, but it’s also busiest. Late spring or early fall can be
                quieter with mild weather.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              The Southern Gulf Islands offer a perfect blend of natural
              splendor, relaxed vibes, and outdoor fun—just a short trip from
              Vancouver. Whether you’re here for a weekend family escape or an
              epic multi-island adventure, you’ll find scenic coastlines,
              friendly artisan communities, and plenty of activities to keep you
              coming back. Start planning your getaway now and discover the
              unique magic that makes these islands such a treasured West Coast
              destination.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}